/* A column's settings, from its header's ⋯ (or Add column): its name and type, what the agent
   should know of it, its rules, a choice's choices, a link's table or what a count counts, and
   its place. Every change is planned as it is made, so the editor says what it does to the rows
   ("Checked 1,284 rows: no repeats", "3 rows have no Email") before Save makes it with that
   plan; a unique rule that repeats block offers to merge them first. */
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";

import { useApplyChanges, useSpaceRepeats } from "@/shared/api/spaces";
import SpacePlanSteps from "@/shared/components/SpacePlanSteps";
import { confirmDangerCls, DangerButton } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton, IconButton } from "@/shared/components/ui-kit";
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/components/ui/popover";
import { Switch } from "@/shared/components/ui/switch";
import { inputCls } from "@/shared/components/task-atoms";
import { cn } from "@/shared/lib/cn";

import { toneOf } from "./table/Cell";
import { keptOnRow } from "./table/columns";
import { usePlannedChange } from "./usePlannedChange";

import type { SpaceChange, SpaceChoice, SpaceColumn, SpaceColumnType, SpaceRule, SpaceTable } from "@/shared/types";

export interface ColumnTarget {
  /** null: a new column. */
  column: SpaceColumn | null;
  /** The header it opened from; null: placed under the toolbar. */
  anchor: DOMRect | null;
}

const TYPES: { type: SpaceColumnType; words: string }[] = [
  { type: "text", words: "Text" }, { type: "long_text", words: "Long text" }, { type: "number", words: "Number" },
  { type: "boolean", words: "Checkbox" }, { type: "date", words: "Date" }, { type: "datetime", words: "Date and time" },
  { type: "choice", words: "Choice" }, { type: "email", words: "Email" }, { type: "url", words: "Web address" },
  { type: "phone", words: "Phone" }, { type: "person", words: "Person" }, { type: "link", words: "Link to a table" },
  { type: "count", words: "Count of linked rows" },
];
const RULES: { rule: SpaceRule; title: string; says: string; fits: (type: SpaceColumnType) => boolean }[] = [
  { rule: "required", title: "Required", says: "A row can't be saved without it.", fits: (type) => type !== "count" },
  {
    rule: "unique", title: "Unique",
    says: "No two rows share it. Compared as a person would: Maya@Lumen.io and maya@lumen.io are one.",
    fits: (type) => ["text", "number", "date", "datetime", "email", "url", "phone"].includes(type),
  },
  {
    rule: "verified_only", title: "Verified only",
    says: "Whoever adds it says who verified it (Apollo, a verifier); unverified emails are refused.",
    fits: (type) => type === "email",
  },
];
const WIDTH = 360;
const fieldCls = inputCls;  // the app's small input (shared/components/task-atoms)

export default function ColumnEditor({ agentId, table, tables, target, onClose }: {
  agentId: string;
  table: SpaceTable;
  tables: SpaceTable[];
  target: ColumnTarget;
  onClose: () => void;
}) {
  const old = target.column;
  const [name, setName] = useState(old?.name ?? "");
  const [type, setType] = useState<SpaceColumnType>(old?.type ?? "text");
  const [note, setNote] = useState(old?.note ?? "");
  const [rules, setRules] = useState<SpaceRule[]>(old?.rules ?? []);
  const [choices, setChoices] = useState<SpaceChoice[]>(old?.options.choices ?? []);
  // another table first; the table itself when it is the only one (a row linking to rows of its own)
  const [linkTable, setLinkTable] = useState(
    old?.options.table ?? (tables.find((t) => !t.system && t.id !== table.id) ?? table).id,
  );
  const [many, setMany] = useState(old?.options.many ?? true);
  // what a count counts: another table's link column pointing here, as "table:column"
  const incoming = tables.flatMap((other) =>
    other.columns
      .filter((column) => column.type === "link" && column.options.table === table.id)
      .map((column) => ({ value: `${other.id}:${column.key}`, words: `${other.name} › ${column.name}` })),
  );
  const [counted, setCounted] = useState(
    old?.type === "count" ? `${old.options.table}:${old.options.column}` : incoming[0]?.value ?? "",
  );
  const [merge, setMerge] = useState(false);
  const [dropping, setDropping] = useState(false);
  const turningUnique = !!old && rules.includes("unique") && !old.rules.includes("unique");

  const changes = useMemo<SpaceChange[] | null>(() => {
    if (dropping && old) return [{ op: "drop_column", table: table.id, column: old.key }];
    if (!name.trim()) return null;
    const [countTable, countColumn] = counted.split(":");
    const options =
      type === "choice" ? { choices }
      : type === "link" ? { table: linkTable, many }
      : type === "count" ? { table: countTable, column: countColumn }
      : {};
    const fits = rules.filter((rule) => RULES.find((entry) => entry.rule === rule)?.fits(type));
    const fields = { name: name.trim(), type, note: note.trim(), rules: fits, options };
    if (!old) return [{ op: "add_column", table: table.id, ...fields }];
    // only what the person changed: an edit that sends the rest unchanged plans the same
    const before = { name: old.name, type: old.type, note: old.note, rules: old.rules, options: old.options };
    const changed = Object.fromEntries(
      Object.entries(fields).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(before[key as keyof typeof before])),
    );
    if (!Object.keys(changed).length) return null;
    const edit: SpaceChange = { op: "edit_column", table: table.id, column: old.key, ...changed };
    return merge && turningUnique ? [{ op: "merge_repeats", table: table.id, column: old.key }, edit] : [edit];
  }, [dropping, old, name, type, note, rules, choices, linkTable, many, counted, merge, turningUnique, table.id]);

  const steps = usePlannedChange(agentId, changes);
  const blockedByRepeats = turningUnique && !!steps.plan.data?.steps.some((step) => step.repeats);
  const repeats = useSpaceRepeats(agentId, table.id, turningUnique && (merge || blockedByRepeats) ? old.key : null);
  const move = useApplyChanges(agentId);
  const place = old ? table.columns.findIndex((column) => column.key === old.key) : -1;
  const moveTo = (position: number) =>
    old && move.mutate({ changes: [{ op: "move_column", table: table.id, column: old.key, position }] });

  // under the header it opened from, or under the toolbar's Add column
  const anchor = target.anchor ?? { left: window.innerWidth - WIDTH - 40, top: 84, width: WIDTH, height: 40 };
  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <div aria-hidden="true" className="fixed pointer-events-none" style={{ left: anchor.left, top: anchor.top, width: anchor.width, height: anchor.height }} />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={16}
        aria-label={old ? `${old.name} column` : "New column"}
        style={{ width: WIDTH }}
        className="p-0 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-menu max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-end gap-2 px-3.5 pt-3 pb-3">
          <label className="flex-1 min-w-0">
            <span className="block mb-1.5 text-[12px] text-[var(--text-tertiary)]">Column</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={fieldCls} aria-label="Column name" />
          </label>
          <label className="w-[150px] shrink-0">
            <span className="block mb-1.5 text-[12px] text-[var(--text-tertiary)]">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as SpaceColumnType)} className={fieldCls} aria-label="Type" disabled={!!old && !keptOnRow(old)}>
              {TYPES.filter((entry) => !old || keptOnRow(entry) === keptOnRow(old)).map((entry) => (
                <option key={entry.type} value={entry.type}>{entry.words}</option>
              ))}
            </select>
          </label>
          {old && (
            <span className="flex shrink-0 pb-0.5">
              <MoveButton label="Move left" disabled={place <= 0 || move.isPending} onClick={() => moveTo(place - 1)} icon={ChevronLeft} />
              <MoveButton label="Move right" disabled={place >= table.columns.length - 1 || move.isPending} onClick={() => moveTo(place + 1)} icon={ChevronRight} />
            </span>
          )}
        </div>
        {type === "choice" && <ChoicesEditor choices={choices} onChoices={setChoices} />}
        {type === "link" && (
          <div className="px-3.5 pb-3 flex items-center gap-2">
            <select value={linkTable} onChange={(e) => setLinkTable(e.target.value)} disabled={!!old} className={cn(fieldCls, "flex-1")} aria-label="Links to">
              {tables.filter((t) => !t.system).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-[12.5px] text-[var(--text-secondary)] shrink-0">
              <Switch checked={many} onCheckedChange={setMany} aria-label="Many rows" /> Many
            </label>
          </div>
        )}
        {type === "count" && (
          <div className="px-3.5 pb-3">
            {incoming.length ? (
              <select value={counted} onChange={(e) => setCounted(e.target.value)} disabled={!!old} className={fieldCls} aria-label="Counts">
                {incoming.map((entry) => <option key={entry.value} value={entry.value}>{entry.words}</option>)}
              </select>
            ) : (
              <p className="m-0 text-[12.5px] text-[var(--text-tertiary)]">No table links to {table.name} yet: a count counts the rows that do.</p>
            )}
          </div>
        )}
        <div className="px-3.5 py-3 border-t hairline">
          <span className="block mb-1.5 text-[12px] text-[var(--text-tertiary)]">For the agent</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            aria-label="What the agent should know of it"
            placeholder="What the agent should know of it, e.g. their work email, never a personal one"
            className={cn(fieldCls, "h-auto py-2 resize-none text-[12.5px] leading-[1.45]")}
          />
        </div>
        <div className="px-3.5 py-2 border-t hairline">
          {RULES.filter((entry) => entry.fits(type)).map((entry) => (
            <label key={entry.rule} className="flex items-start gap-3 py-[7px] cursor-pointer">
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-medium text-[var(--text-primary)]">{entry.title}</span>
                <span className="block mt-0.5 text-[12px] leading-[1.4] text-[var(--text-tertiary)]">{entry.says}</span>
              </span>
              <Switch
                checked={rules.includes(entry.rule)}
                onCheckedChange={(on) => setRules(on ? [...rules, entry.rule] : rules.filter((rule) => rule !== entry.rule))}
                aria-label={entry.title}
                className="mt-0.5"
              />
            </label>
          ))}
        </div>
        {turningUnique && (merge || blockedByRepeats) && (
          <label className="flex items-start gap-3 px-3.5 py-2.5 border-t hairline cursor-pointer">
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium text-[var(--text-primary)]">Merge repeats first</span>
              <span className="block mt-0.5 text-[12px] leading-[1.4] text-[var(--text-tertiary)]">
                {repeated(repeats.data ?? [])} Each group becomes its oldest row; links to the others move to it.
              </span>
            </span>
            <Switch checked={merge} onCheckedChange={setMerge} aria-label="Merge repeats first" className="mt-0.5" />
          </label>
        )}
        {changes && (
          <div className="border-t hairline">
            <SpacePlanSteps
              plan={steps.plan.data}
              pending={steps.plan.isFetching || steps.waiting}
              error={steps.plan.error ? steps.plan.error.message : null}
            />
          </div>
        )}
        <div className="flex items-center gap-2 px-3 py-2.5 border-t hairline">
          {old && old.key !== table.columns[0]?.key && (dropping
            ? <GhostButton onClick={() => setDropping(false)}>Keep column</GhostButton>
            : <DangerButton onClick={() => setDropping(true)}>Delete column</DangerButton>)}
          <span className="flex-1" />
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          {dropping ? (
            <button type="button" onClick={() => steps.make(onClose)} disabled={!steps.ready || steps.pending} className={confirmDangerCls}>
              Delete
            </button>
          ) : (
            <BrandButton onClick={() => steps.make(onClose)} disabled={!steps.ready || steps.pending} className="disabled:opacity-40 disabled:pointer-events-none">
              {old ? "Save" : "Add column"}
            </BrandButton>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** The repeated values in a line: "Lumen ×2, Juniper ×3 and 4 more." */
function repeated(groups: { rows: { name: string | null }[] }[]): string {
  if (!groups.length) return "";
  const shown = groups.slice(0, 3).map((group) => `${group.rows[0]?.name || "Untitled"} ×${group.rows.length}`);
  const more = groups.length > 3 ? ` and ${groups.length - 3} more` : "";
  return `${shown.join(", ")}${more}.`;
}

function MoveButton({ label, disabled, onClick, icon: Icon }: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  icon: typeof ChevronLeft;
}) {
  return (
    <IconButton aria-label={label} title={label} disabled={disabled} onClick={onClick} className="disabled:opacity-35 disabled:pointer-events-none">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
    </IconButton>
  );
}

/** A choice column's choices: each a tag in its tone, renamed in place, removed with ×. */
function ChoicesEditor({ choices, onChoices }: { choices: SpaceChoice[]; onChoices: (choices: SpaceChoice[]) => void }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const name = draft.trim();
    if (!name || choices.some((choice) => choice.name.toLowerCase() === name.toLowerCase())) return;
    onChoices([...choices, { name, tone: toneOf(name) }]);
    setDraft("");
  };
  return (
    <div className="px-3.5 pb-3">
      <div className="flex flex-wrap gap-1.5">
        {choices.map((choice, index) => (
          <span key={`${index}:${choice.name}`} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-[8px] bg-[var(--surface-2)] text-[12.5px] text-[var(--text-secondary)]">
            <span className="w-2 h-2 rounded-full" style={{ background: `var(--tone-${choice.tone}-fg)` }} aria-hidden="true" />
            {choice.name}
            <IconButton aria-label={`Remove ${choice.name}`} onClick={() => onChoices(choices.filter((_, at) => at !== index))} className="!w-5 !h-5 !rounded-[6px]">
              <X className="w-3 h-3" strokeWidth={2} />
            </IconButton>
          </span>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            add();
          }}
          placeholder="Add a choice"
          aria-label="New choice"
          className={cn(fieldCls, "flex-1")}
        />
        <IconButton size="md" aria-label="Add choice" title="Add choice" onClick={add}>
          <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
        </IconButton>
      </div>
    </div>
  );
}
